import ButtonC, { BUTTON_APPEARANCE, BUTTON_HOVERSTYLE } from 'components/ButtonC';
import { ICON_SIZE } from 'components/IconC';
import { LanguageContext } from 'providers/Language';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { memo, useCallback, useContext, useEffect, useRef, useState } from 'react';

type MediaAnimationVideoProps = {
  top: number;
  left: number;
  width: number;
  height: number;
  displayType: ALinkDisplayTypes;
  animationUrl?: string;
  rclAnimationDetails?: ARclAnimationDetails;
};

const MediaAnimationVideo = memo(
  ({
    top,
    left,
    width,
    height,
    displayType,
    animationUrl,
    rclAnimationDetails,
  }: MediaAnimationVideoProps) => {
    const { t } = useContext(LanguageContext);
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isVideoLoaded, setIsVideoLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);

    const hasVideoSources =
      displayType === 'rendercloud_animation'
        ? Boolean(rclAnimationDetails?.assets?.length)
        : Boolean(animationUrl);

    const hasVideoControl = Boolean(rclAnimationDetails?.videoControl && hasVideoSources);

    const handleVideoLoaded = useCallback(() => {
      setIsVideoLoaded(true);
    }, []);

    const handleVideoError = useCallback(() => {
      setHasError(true);
      setIsPlaying(false);
    }, []);

    useEffect(() => {
      const video = videoRef.current;
      if (video && video.readyState >= 1) {
        setIsVideoLoaded(true);
      }

      const handleOtherPlay = (e: Event) => {
        if (hasVideoControl && video && e.target !== video && !video.paused) {
          video.pause();
        }
      };

      const handleVisibilityChange = () => {
        if (document.hidden && video && !video.paused) {
          video.pause();
        }
      };

      document.addEventListener('play', handleOtherPlay, true);
      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => {
        document.removeEventListener('play', handleOtherPlay, true);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        if (video) {
          video.pause();
        }
      };
    }, [hasVideoControl]);

    const handleTogglePlay = useCallback((e?: ReactMouseEvent) => {
      if (e) {
        e.stopPropagation();
      }
      const video = videoRef.current;
      if (!video) return;

      if (video.paused || video.ended) {
        if (video.ended) {
          video.currentTime = 0;
        }
        video.muted = false;
        video
          .play()
          .then(() => {
            setIsPlaying(true);
          })
          .catch(() => {
            setIsPlaying(false);
          });
      } else {
        video.pause();
        setIsPlaying(false);
      }
    }, []);

    const shouldLoop =
      !hasVideoControl &&
      (displayType === 'rendercloud_animation' ? Boolean(rclAnimationDetails?.loop) : true);

    const playIcon =
      import.meta.env.VITE_THEME === 'lidl' ? 'play-circle' : `play--${import.meta.env.VITE_THEME}`;
    const videoIcon = isPlaying ? 'pause' : playIcon;

    if (!hasVideoSources || hasError) {
      return null;
    }

    return (
      <div
        style={{
          top: `${top}%`,
          left: `${left}%`,
          width: `${width}%`,
          height: `${height}%`,
        }}
        className={`mediagroup mediagroup--video-wrapper ${
          !hasVideoControl ? 'mediagroup--background' : ''
        }`}>
        {/* eslint-disable jsx-a11y/media-has-caption */}
        <video
          ref={videoRef}
          className={`mediagroup--video ${isVideoLoaded ? 'mediagroup--video-loaded' : ''}`}
          preload={hasVideoControl ? 'metadata' : 'auto'}
          onLoadedMetadata={handleVideoLoaded}
          onLoadedData={handleVideoLoaded}
          onCanPlay={handleVideoLoaded}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => setIsPlaying(false)}
          onError={handleVideoError}
          onClick={hasVideoControl ? handleTogglePlay : undefined}
          autoPlay={!hasVideoControl}
          muted={!hasVideoControl}
          loop={shouldLoop}
          controls={false}
          playsInline
          aria-hidden={hasVideoControl}>
          {displayType === 'rendercloud_animation' ? (
            rclAnimationDetails?.assets?.map((asset) => (
              <source key={asset.url} src={asset.url} type={asset.type} />
            ))
          ) : (
            <source src={animationUrl} type="video/mp4" />
          )}
        </video>

        {hasVideoControl && isVideoLoaded && (
          <div
            className={`mediagroup--video-control-button ${
              isPlaying ? 'mediagroup--video-control-button--playing' : ''
            }`}>
            <ButtonC
              type={BUTTON_APPEARANCE.MEDIA}
              hoverStyle={BUTTON_HOVERSTYLE.BACKGROUND}
              iconSize={ICON_SIZE.M}
              icon={videoIcon}
              onClick={handleTogglePlay}
              ariaLabel={isPlaying ? t('pauseVideo') : t('playVideo')}
            />
          </div>
        )}
      </div>
    );
  },
);

export default MediaAnimationVideo;
